import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHelpCircle, FiDollarSign, FiShield, FiFileText, FiTrendingUp, FiLock, FiChevronDown, FiChevronUp } = FiIcons;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      icon: FiDollarSign,
      question: "How is this free?",
      answer: "We want to provide helpful services for users and offer other subscription-based services. Our goal is to help filmmakers access global incentives that might otherwise be difficult to find."
    },
    {
      icon: FiShield,
      question: "Is our data private?",
      answer: "We use third-party AI services to analyze and generate content for this service. Your project data is processed securely and we follow strict privacy guidelines."
    },
    {
      icon: FiFileText,
      question: "What files can I upload?",
      answer: "You can upload PDF or TXT files up to 20MB. We support most script formats and project descriptions for comprehensive analysis."
    },
    {
      icon: FiTrendingUp,
      question: "How accurate are the estimates?",
      answer: "Estimates are based on current incentive rates and typical local spend requirements. Always verify details directly with the relevant film commission for the most accurate information."
    },
    {
      icon: FiLock,
      question: "Do you store my files?",
      answer: "We temporarily process your files for analysis but do not permanently store them. Any data retention is minimal and follows industry privacy standards."
    }
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section className="section-spacing bg-gray-50">
      <div className="container-responsive">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about using NuPitch Incentive Match Maker
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={faq.icon} className="w-5 h-5 text-primary-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                  </div>
                  <SafeIcon
                    icon={openIndex === index ? FiChevronUp : FiChevronDown}
                    className="w-5 h-5 text-gray-500 transition-transform"
                  />
                </button>

                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="text-center mt-12"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Still have questions?</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Can't find the answer you're looking for? We're here to help!
              </p>
              <a
                href="mailto:info@nupitchtechnologies.com?subject=NuPitch Incentive Match Maker Question"
                className="inline-flex items-center space-x-2 bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
              >
                <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
                <span>Contact Support</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
